import type { FC } from 'react';

import type { SegmentEntityBase } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';

import { errorMessage, successMessage } from '../../ducks/messages/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import {
  SEGMENT_TYPE_COMMENT,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
} from '../segment_form/constants';
import { SegmentInputComment } from './segment_input_comment';
import { SegmentInputGeneric } from './segment_input_generic';
import { SegmentInputTag } from './segment_input_tag';
import type { SegmentInputCommonProps } from './types';

type Props = SegmentInputCommonProps & {
  segment: SegmentEntityBase;
  placeholder?: string;
  required?: boolean;
  onChange: (segment: SegmentEntityBase) => void;
  autoFocus?: boolean;
};

export const SegmentInput: FC<Props> = ({
  segment,
  disabled,
  onLayoutChanged,
  onSubmitRequested,
  onClick,
  onChange,
  required,
  autoFocus,
}) => {
  const dispatch = useAppDispatch();
  const tagValue = segment.metadata?._gcvi_tags ?? [];
  const handleTagChange = (value: any[]) =>
    onChange({
      ...segment,
      metadata: {
        ...segment.metadata,
        _gcvi_tags: value,
      },
    });

  if (segment.segment_type === SEGMENT_TYPE_COMMENT) {
    return (
      <SegmentInputComment
        segmentType={segment.segment_type}
        value={segment.segment_text ?? ''}
        disabled={disabled}
        onLayoutChanged={onLayoutChanged}
        onSubmitRequested={onSubmitRequested}
        onClick={onClick}
        color={segment.segment_color}
        onChange={(value) => onChange({ ...segment, segment_text: value })}
        required={required}
        autoFocus={autoFocus}
      />
    );
  }

  if (segment.segment_type === SEGMENT_TYPE_TAG) {
    return (
      <SegmentInputTag
        segmentType={segment.segment_type}
        value={tagValue}
        disabled={disabled}
        onLayoutChanged={onLayoutChanged}
        onSubmitRequested={onSubmitRequested}
        onClick={onClick}
        onChange={handleTagChange}
        onValuesCopied={() =>
          dispatch(successMessage({ text: 'Values copied' }))
        }
        onCopyError={(text) => dispatch(errorMessage({ text }))}
      />
    );
  }

  if (segment.segment_type === SEGMENT_TYPE_GENERIC) {
    return (
      <SegmentInputGeneric
        segmentType={segment.segment_type}
        metadataViewId={segment.metadata_view_id ?? undefined}
        value={segment.metadata ?? {}}
        disabled={disabled}
        onLayoutChanged={onLayoutChanged}
        onSubmitRequested={onSubmitRequested}
        onClick={onClick}
        onChange={(value) => onChange({ ...segment, metadata: value })}
      />
    );
  }

  return null;
};
